import _ from 'lodash';

import { AddressString } from '~components/providers/types';
import { CONTRACT_ADDRESS_LOOKUP_CFG, contractConfig, ContractNames } from '~config/protocol/contractConfig';
import { NetworkIds } from '~config/rpcConfig';
import {
  AllVestaAssets,
  BoostedFarm,
  ELPType,
  ENativeAsset,
  EOtherAsset,
  EVestaAsset,
  FarmTypes,
  RewardedVaults,
  RewardedVaultsValues,
} from '~config/types';
import { makeThrowable } from '~utils/error/errorHandlers';

import { ASSET_CONFIG, coingeckoConfig } from './assetConfig';
import { COLLATERAL_CONFIG } from './assetConfig/collaterals';
import { FARM_CONFIGURATION } from './assetConfig/farms';
import { ConfigAsset, ConfigCollateral, ConfigFarm } from './assetConfig/types';

export const getContractAddress = (name: ContractNames, networkId: NetworkIds) => {
  const selectedNetwork = networkId in contractConfig ? networkId : NetworkIds.ARBITRUM_ONE;

  const address = contractConfig[selectedNetwork][name];
  return address;
};

export function checkValidContractAddress(addressOrAddresses: AddressString | AddressString[]) {
  if (typeof addressOrAddresses === 'string') {
    return addressOrAddresses !== '0x';
  }

  return addressOrAddresses.reduce((acc, curr) => acc && curr !== '0x', true);
}

export function getAssetConfig(asset: AllVestaAssets): ConfigAsset {
  if (ASSET_CONFIG[asset]) return ASSET_CONFIG[asset] as ConfigAsset;
  throw makeThrowable('No such asset config: ' + asset);
}

export function getCollateralConfig(asset: EVestaAsset): ConfigCollateral {
  if (COLLATERAL_CONFIG[asset]) return COLLATERAL_CONFIG[asset] as ConfigCollateral;
  throw makeThrowable('No such collateral config: ' + asset);
}

export function getFarmConfig(farm: FarmTypes | BoostedFarm): ConfigFarm {
  if (FARM_CONFIGURATION[farm]) return FARM_CONFIGURATION[farm] as ConfigFarm;
  throw makeThrowable('No such farm config: ' + farm);
}

export const ActiveCollateralAssets = _.compact(
  Object.values(EVestaAsset).map(asset => (getCollateralConfig(asset).disabled ? undefined : asset)),
) as EVestaAsset[];

export const RewardedCollateralAssets = _.compact(
  Object.values(EVestaAsset).map(asset => (getCollateralConfig(asset).isVaultRewarded ? asset : undefined)),
) as RewardedVaults[];

export const getAssetIcon = (asset: AllVestaAssets, lightOrDark: 'light' | 'dark') =>
  `/v2/assets/${asset}-${lightOrDark}.svg`;

export function getAssetFromAddress<T extends string | string[]>(
  assetAddr: T,
  networkId: NetworkIds,
): T extends string[] ? AllVestaAssets[] : AllVestaAssets {
  const lookup =
    networkId in NetworkIds
      ? CONTRACT_ADDRESS_LOOKUP_CFG[networkId]
      : CONTRACT_ADDRESS_LOOKUP_CFG[NetworkIds.ARBITRUM_ONE];

  if (_.isArray(assetAddr)) {
    const assetArray: AllVestaAssets[] = [];

    for (const assetAddress of assetAddr) {
      if (assetAddress.toLowerCase() in lookup)
        assetArray.push(lookup[assetAddress.toLowerCase()] as AllVestaAssets);
      else {
        console.warn('looked up an invalid address... returning VSTA as the asset');
        assetArray.push(ENativeAsset.VSTA);
      }
    }

    return assetArray as any;
  }

  if (typeof assetAddr === 'string') {
    if (assetAddr.toLowerCase() in lookup) return lookup[assetAddr.toLowerCase()] as any;
  }
  throw makeThrowable('No such token address: ' + assetAddr);
}

export const getReadableSpRewardFromAddress = (rewardAddress: string, networkId: NetworkIds) => {
  let wrappedReward = getAssetFromAddress(rewardAddress, networkId);

  if (wrappedReward === EOtherAsset.vwDPX) {
    wrappedReward = EVestaAsset.DPX;
  } else if (wrappedReward === EOtherAsset.vwGMX) {
    wrappedReward = EVestaAsset.GMX;
  }

  return wrappedReward;
};

export const getLpFromFarm = (farm: FarmTypes | BoostedFarm): ELPType => {
  if (farm in FARM_CONFIGURATION) return FARM_CONFIGURATION[farm].lpToken as ELPType;
  throw makeThrowable('No such Farm: ' + farm);
};

export function getAssetFromCoingeckoID(cgId: string) {
  if (cgId in coingeckoConfig) return coingeckoConfig[cgId];
  throw makeThrowable('No such coingecko id: ' + cgId);
}

export const isRewardedVault = (x: any): x is RewardedVaults => RewardedVaultsValues.includes(x);
